import BookingDateTime from "@/app/components/ui/Reusable/BookingDateTime";
import BookingStartDate from "@/app/components/ui/Reusable/BookingStartDate";
import Breed from "@/app/components/ui/Reusable/Breed";
import CustomerProfileRedirect from "@/app/components/ui/Reusable/CustomerProfileRedirect";
import PetName from "@/app/components/ui/Reusable/PetName";
import ServiceName from "@/app/components/ui/Reusable/ServiceName";
import Species from "@/app/components/ui/Reusable/Species";
import SubServiceName from "@/app/components/ui/Reusable/SubServiceName";
import { useIsBookingChargableAPI } from "@/app/hooks/api_hooks/bookings/useIsBookingChargableAPI";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useUtils } from "@/app/hooks/useUtils";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { setModalStateData } from "@/app_redux/reducers/modal_state_reducer";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { ISlotsState } from "@/app_redux/reducers/slices/auth/slots_slice";
import clsx from "clsx";
import { useEffect } from "react";
import { BsCurrencyRupee } from "react-icons/bs";
import { useSearchParams } from "react-router-dom";

const DetailsBlock: React.FC<{ label: string; value: React.ReactNode; className?: string }> = ({
  label,
  value,
  className,
}) => {
  return (
    <div className={clsx("flex items-center gap-1.5", className)}>
      <span className="text-gray-400 text-[11px] font-medium shrink-0">{label}:</span>
      <div className="flex justify-start items-center text-gray-800 text-xs font-medium truncate">{value}</div>
    </div>
  );
};

const SlotsRescheduleDetailsComponent = () => {
  const { getRouteKey } = useRouter();
  const { routeState } = useRouteInfo(getRouteKey("SLOTS", "id"));
  const { HTMLLiTagsToArray,getDisplayStaffName } = useUtils();
  const { customerDetails, customerId, petId } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"))?.routeState
    .state as ICustomerProfileState;
  const dispatch = useAppDispatch();
  const { setVisible } = useModalActions("CUSTOMER_PROFILE_APPOINTMENTS_TABLE_COMMENTS_VIEW_MORE");
  const { execute: logClick } = useClickLogAPI();
  const vmsUserId = useAppSelector((state) => state.auth?.user?.email);

  const [params, _setParams] = useSearchParams();

  const { rescheduledBooking: bookingDetails } = routeState.state as ISlotsState;
  const { execute: fetchIsBookingCharged, bookingDetails: rescheduleBookingPaymentDetails } =
    useIsBookingChargableAPI();

  const handleCommentView = (notes: string) => {
    logClick({
      event_type: "RESCHEDULE_PREVIOUS_COMMENT_VIEW_CLICK",
      scr_id: "RESCHEDULE_PREVIEW",
      client_id: customerId,
      pet_id: petId,
      vms_user_id: vmsUserId,
      timestamp: Date.now(),
    });
    dispatch(
      setModalStateData({
        key: "CUSTOMER_PROFILE_APPOINTMENTS_TABLE_COMMENTS_VIEW_MORE",
        data: {
          appointmentNotes: notes,
        },
      }),
    );
    setVisible();
  };

  useEffect(() => {
    const rescheduleBookingId = params?.get("rbi");
    if (rescheduleBookingId) {
      fetchIsBookingCharged(rescheduleBookingId);
    }
  }, [params?.get("rbi")]);

  return (
    bookingDetails && (
      <div className="w-full">
        <div className="mb-2">
          <p className="text-gray-900 font-semibold text-sm">Reschedule Booking</p>
          <p className="text-gray-500 text-xs">Previous Appointment Details</p>
        </div>
        <div className="gap-x-6 gap-y-1.5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 w-full">
          <DetailsBlock
            label="Pet Name"
            value={<PetName className="text-xs p-0 m-0" petName={bookingDetails?.pet_details?.name} petStatus="" />}
          />
          <DetailsBlock
            label="Service"
            value={
              <div className="text-xs">
                <ServiceName serviceId={bookingDetails?.service_type_id} />,
                <SubServiceName
                  serviceId={bookingDetails?.service_type_id}
                  subServiceId={bookingDetails?.subservice_id}
                  className="ml-1"
                />
              </div>
            }
          />
          <DetailsBlock
            label="Date"
            value={<BookingStartDate className="text-xs" startDate={bookingDetails?.booking_start_date} />}
          />
          <DetailsBlock
            label="Pet Parent"
            value={<CustomerProfileRedirect label={customerDetails?.name} />}
          />
          <DetailsBlock
            label="Clinic"
            value={<span className="text-xs"> {bookingDetails?.clinic_details?.display_name} </span>}
          />
          <DetailsBlock
            label="Time"
            value={
              <div className="text-xs">
                <BookingDateTime startDateTime={bookingDetails?.booking_start_date_time} />{" "}
                <span className="ml-1 mr-1">to</span>
                <BookingDateTime className="ml-1" startDateTime={bookingDetails?.booking_end_date_time} />
              </div>
            }
          />
          <DetailsBlock
            label="Breed"
            value={
              <div className="text-xs">
                <Species speciesId={bookingDetails?.pet_details?.pet_species_type_id} />
                <span className="ml-1 mr-1">,</span>
                <Breed
                  breedId={parseInt(bookingDetails?.pet_details?.pet_breed_id)}
                  speciesId={bookingDetails?.pet_details?.pet_species_type_id}
                />
              </div>
            }
          />
          <DetailsBlock
            label="Provider"
value={
  <div className="text-xs">
    <span>{getDisplayStaffName(bookingDetails) || "NA"}</span>
  </div>
}
          />
          <DetailsBlock
            label="Comments"
            value={
              <div className="text-xs w-full flex justify-start items-center">
                <span className="block w-28 overflow-hidden truncate">
                  {HTMLLiTagsToArray(bookingDetails?.appointment_note)[0]}
                </span>
                <button
                  onClick={() => {
                    handleCommentView(bookingDetails?.appointment_note);
                  }}
                  className="text-blue-600 ml-2"
                >
                  View More
                </button>
              </div>
            }
          />

          {rescheduleBookingPaymentDetails && (
            <DetailsBlock
              label="Amount"
              value={
                <div className="text-xs">
                  {" "}
                  <span className="flex items-center gap-1">
                    {" "}
                    <BsCurrencyRupee className="w-3 h-3" /> {rescheduleBookingPaymentDetails?.amount}
                  </span>{" "}
                </div>
              }
            />
          )}
        </div>
      </div>
    )
  );
};

export default SlotsRescheduleDetailsComponent;
